import React, { useEffect, useMemo, useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	CircularProgress,
	Divider,
	FormControlLabel,
	Stack,
	Switch,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowDropdown, ArrowUpIcon, BookmarkIcon, DeleteIcon, SearchIcon } from "../../../assets/icons";
import {
	buildAppliedFilterPayload,
	filtersToPayload,
} from "../../../common/FiltersFormEngine/mappers/filtersToPayload";
import {
	getAppliedFiltersCount,
	isAdvancedFiltersValid,
	isFilterNameAvailable,
	isSimpleFiltersValid,
} from "../../../common/FiltersFormEngine/querySelectors";
import { useFiltersRegistry } from "../../../context/FiltersProvider";
import { getDashboardFilters } from "../../../context/FiltersProvider/selectors/dashboardSelectors";
import { UserCustomFilterDTO, UserCustomFilterPayload } from "../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../datasources/networking/apiRoutes";
import { useNavigationPreferences } from "../../../hooks/usePreferences";
import { useWriteResource } from "../../../hooks/useResource";
import { FilterContexType } from "../../layout/ViewHeader";
import AdvancedFiltersModal from "../AdvanceFilterModal";
import { DashboardAdvancedModeModal } from "./components/DashboardAdvancedModeDialog";
import { DashboardFilterItem } from "./components/DashboardFilterItem";
import { FilterNamePopover } from "./components/FilterNamePopover";

type DashboardProps = {
	filterContext?: FilterContexType;
};

export const Dashboard = ({ filterContext }: DashboardProps) => {
	const { t } = useTranslation("common");
	const theme = useTheme();
	const { navigationPreferences } = useNavigationPreferences();

	const {
		registry,
		dashboardFilters,
		clearDashboardFilters,
		addCustomFilter,
		advancedMode,
		enableAdvancedMode,
		disableAdvancedMode,
		advancedForm,
		initializeAdvancedForm,
		isHydrating,
		appliedFilters,
		applyFilters,
		updateDashboardFilter,
		removeDashboardFilter,
	} = useFiltersRegistry();

	const [expanded, setExpanded] = useState(!isHydrating);
	const [openDashboardAdvancedModal, setOpenDashboardAdvancedModal] = useState(false);

	const [saveAnchorEl, setSaveAnchorEl] = useState<HTMLElement | null>(null);

	// Add "entities" to filter context if logical perspective is selected
	const effectiveFilterContext = useMemo<FilterContexType>(() => {
		if (!filterContext) return [];

		return navigationPreferences.transportsPerspective === "logical"
			? [...filterContext, "entities"]
			: filterContext;
	}, [filterContext, navigationPreferences.transportsPerspective]);

	useEffect(() => {
		if (!isHydrating) {
			setExpanded(true);
		} else {
			setExpanded(false);
		}
	}, [isHydrating]);

	// Resolve dashboard runtime filters with registry definitions
	const filters = useMemo(() => {
		return getDashboardFilters(registry, dashboardFilters, effectiveFilterContext);
	}, [registry, dashboardFilters, effectiveFilterContext]);

	const handleToggleAccordion = () => {
		setExpanded((prev) => !prev);
	};

	const handleClearAll = () => {
		clearDashboardFilters();
		disableAdvancedMode();
	};

	const handleConfirmAdvancedMode = () => {
		initializeAdvancedForm();
		enableAdvancedMode();
		setOpenDashboardAdvancedModal(false);
	};

	const handleOpenPopoverSave = (event: React.MouseEvent<HTMLButtonElement>) => {
		setSaveAnchorEl(event.currentTarget);
	};

	const handleClosePopoverSave = () => {
		setSaveAnchorEl(null);
	};

	const { post: saveCustomFilterPost, isLoading: saveCustomFilterLoading } = useWriteResource<
		UserCustomFilterPayload,
		UserCustomFilterDTO
	>(
		{ route: ApiRoutes.CustomFilter },
		{ subjectKey: "customfilter" }
	);

	const handleSave = async (name: string) => {
		const payload = filtersToPayload({
			name,
			mode: advancedMode ? "advanced" : "simple",
			dashboardFilters,
			advancedForm,
		});

		const saved = await saveCustomFilterPost(payload);

		addCustomFilter({
			...saved,
			group: advancedMode ? "configurations" : "saved",
			filterType: advancedMode ? "configuration" : "simple",
			presentation: advancedMode ? "builder" : "chip",
			ui: {
				canPin: false,
				hasMenu: true,
			},
		});

		handleClosePopoverSave();
	};

	const handleApply = () => {
		const payload = buildAppliedFilterPayload({
			mode: advancedMode ? "advanced" : "simple",
			dashboardFilters,
			advancedForm,
		});

		applyFilters(payload);

		setExpanded(false);
	};

	const configurationValidated = isSimpleFiltersValid(dashboardFilters) || isAdvancedFiltersValid(advancedForm);

	const appliedCount = getAppliedFiltersCount(appliedFilters.payload);

	const modalParagraphs = t("dashboard/list") !== "dashboard/list" 
		? t("dashboard/list", { returnObjects: true }) as string[]
		: []

	if (!registry) {
		return null;
	}

	return (
		<Accordion
			expanded={expanded}
			onChange={handleToggleAccordion}
			elevation={3}
			sx={{ borderRadius: "4px", mb: 2 }}
		>
			<AccordionSummary
				sx={{
					"& .MuiAccordionSummary-content": {
						margin: 0,
						border: `1px solid ${theme.palette.grey[200]}`,
						boxShadow: `0px 3px 3px -2px ${theme.palette.grey[200]}`,
						borderRadius: "4px",
					},

					"& .MuiAccordionSummary-content.Mui-expanded": {
						margin: 0,
					},
				}}
			>
				<Stack direction="row" alignItems="center" justifyContent="space-between" p={2} width={"100%"}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography color="primary" variant="subtitle2">
							{t("applied filters")}
						</Typography>

						{isHydrating ? (
							<CircularProgress sx={{ ml: 2 }} size={18} />
						) : (
							<Box
								sx={{
									minWidth: 22,
									height: 22,
									borderRadius: "50%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									backgroundColor: theme.palette.grey[200],
									px: 0.5,
								}}
							>
								<Typography variant="caption" color="primary" fontWeight={700}>
									{appliedCount}
								</Typography>
							</Box>
						)}
					</Stack>

					{expanded ? <ArrowUpIcon /> : <ArrowDropdown />}
				</Stack>
			</AccordionSummary>

			<AccordionDetails sx={{ pt: 0, mx: 2 }}>
				<Stack direction="row" flexWrap="wrap" py={2} gap={1}>
					{!advancedMode &&
						filters.map(({ instance, filter, isAvailable }) => (
							<DashboardFilterItem
								key={instance.id}
								instance={instance}
								filter={filter}
								disabled={!isAvailable}
								onUpdate={updateDashboardFilter}
								onRemove={removeDashboardFilter}
							/>
						))}

					{advancedMode && (
						<AdvancedFiltersModal filterContext={effectiveFilterContext} items={advancedForm} />
					)}
				</Stack>

				{/* Footer */}
				<Divider />

				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					flexWrap="wrap"
					gap={2}
					py={2}
				>
					{/* Left side */}
					<Tooltip title={advancedMode ? t("advanced mode enabled text") : ""} placement="top">
						<span>
							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={advancedMode}
										onChange={() => setOpenDashboardAdvancedModal(true)}
										disabled={advancedMode}
									/>
								}
								label={<Typography variant="body2">{t("advanced mode")}</Typography>}
								labelPlacement="start"
								sx={{ m: 0 }}
							/>
						</span>
					</Tooltip>

					<Stack direction="row" spacing={1}>
						<Button
							color="error"
							startIcon={<DeleteIcon color="error" />}
							onClick={handleClearAll}
							size="small"
						>
							{t("remove all")}
						</Button>

						<Button
							startIcon={saveCustomFilterLoading ? <CircularProgress size={18} /> : <BookmarkIcon />}
							onClick={(e) => handleOpenPopoverSave(e)}
							disabled={!configurationValidated || saveCustomFilterLoading}
							size="small"
						>
							{advancedMode ? t("save configuration") : t("save filter")}
						</Button>

						<Button
							variant="contained"
							startIcon={
								saveCustomFilterLoading ? (
									<CircularProgress size={18} />
								) : (
									<SearchIcon color="inherit" />
								)
							}
							disabled={!configurationValidated || saveCustomFilterLoading}
							onClick={handleApply}
							size="small"
						>
							{t("apply")}
						</Button>
					</Stack>
				</Stack>
			</AccordionDetails>

			<DashboardAdvancedModeModal
				title={t("dashboard/title")}
				paragraphs={modalParagraphs}
				confirmText={t("dashboard/confirm")}
				open={openDashboardAdvancedModal}
				onClose={() => setOpenDashboardAdvancedModal(false)}
				onConfirm={handleConfirmAdvancedMode}
			/>

			<FilterNamePopover
				title={advancedMode ? t("save configuration") : t("save filter")}
				anchorEl={saveAnchorEl}
				open={Boolean(saveAnchorEl)}
				onClose={handleClosePopoverSave}
				onSave={handleSave}
				validateName={(value) =>
					isFilterNameAvailable({
						registry,
						name: value,
					})
				}
			/>
		</Accordion>
	);
};
